import { useMemo } from 'react';

import {
    useCoverageMapAvailability,
    useCoverageMapPoints,
} from '@apps/web/features/coverage/api/coverage_queries';
import { requestErrorMessage } from '@apps/web/features/coverage/model/request_model';

export type CoverageMapStatus = 'idle' | 'loading' | 'error' | 'ready';
export function useCoverageMap({
    organizationId,
    enabled,
    includeInactive,
    includeDoNotContact,
}: {
    readonly organizationId: string | null;
    readonly enabled: boolean;
    readonly includeInactive: boolean;
    readonly includeDoNotContact: boolean;
}) {
    const { data, error, hasRequestFailed, isLoading, mutate } =
        useCoverageMapPoints({
            organizationId,
            enabled,
            includeInactive,
            includeDoNotContact,
        });
    const status: CoverageMapStatus =
        !enabled || !organizationId
            ? 'idle'
            : data !== undefined
              ? 'ready'
              : isLoading
                ? 'loading'
                : hasRequestFailed
                  ? 'error'
                  : 'idle';
    // The map response carries statuses for first paint; the minute poll
    // then overlays fresher ones. A caregiver missing from the poll is unknown.
    const availability = useCoverageMapAvailability({
        organizationId,
        enabled: enabled && status === 'ready',
        includeInactive,
    });
    const polledStatuses = availability.data?.statuses;
    const points = useMemo(() => {
        const loadedPoints = status === 'ready' ? (data?.points ?? []) : [];
        if (!polledStatuses) {
            return loadedPoints;
        }
        const statusById = new Map(
            polledStatuses.map((entry) => [
                entry.caregiver_id,
                entry.availability_status,
            ]),
        );
        return loadedPoints.map((point) => ({
            ...point,
            availability_status:
                statusById.get(point.caregiver_id) ?? 'unknown',
        }));
    }, [status, data, polledStatuses]);
    return {
        status,
        hasRequestFailed,
        errorMessage:
            status === 'error' && error
                ? requestErrorMessage(
                      error,
                      'We couldn’t load caregiver locations.',
                  )
                : null,
        refreshErrorMessage:
            status === 'ready' && (error || availability.error)
                ? 'We couldn’t refresh caregiver locations or availability. Showing previously loaded data.'
                : null,
        points,
        retry: () => {
            void mutate();
            void availability.mutate();
        },
    };
}
